class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = null;
    this.closeButton = null;
  }

  connectedCallback() {
    this.drawer = this.querySelector('theme-drawer');
    this.closeButton = this.querySelector('[data-cart-drawer-close]');
    this.openOnAdd = this.dataset.openOnAdd !== 'false';

    this.closeButton?.addEventListener('click', () => this.drawer?.close());

    this._subscribeToEvents();
    this.applyActiveFooterError();
  }

  open() {
    this.drawer?.open();
  }

  close() {
    this.drawer?.close();
  }

  disconnectedCallback() {
    this._cartAddUnsub?.();
    this._drawerOpenUnsub?.();
    this._drawerCloseUnsub?.();
    this._footerErrorUnsub?.();
    this._cartUpdateUnsub?.();
    this._pageRestoredUnsub?.();
  }

  _subscribeToEvents() {
    this._cartAddUnsub = theme.pubsub.subscribe(theme.pubsub.events.CART_ADD, (data) => {
      if (!this.openOnAdd || data?.openDrawer === false) return;
      this.drawer?.open();
    });
    this._drawerOpenUnsub = theme.pubsub.subscribe(theme.pubsub.events.CART_DRAWER_OPEN, () => this.drawer?.open());
    this._drawerCloseUnsub = theme.pubsub.subscribe(theme.pubsub.events.CART_DRAWER_CLOSE, () => this.drawer?.close());
    this._cartUpdateUnsub = theme.pubsub.subscribe(theme.pubsub.events.CART_UPDATE, (data) => {
      this._handleCartUpdate(data);
    });
    this._footerErrorUnsub = theme.pubsub.subscribe(theme.pubsub.events.CART_FOOTER_ERROR, (data) => this._handleFooterError(data));
    this._pageRestoredUnsub = theme.pubsub.subscribe(theme.pubsub.events.PAGE_RESTORED, () => {
      theme.cart.fetch()
        .then((cart) => this._updateHeaderBadge(cart))
        .catch((error) => console.error('[cart][silent]', error));
    });
  }

  _handleFooterError({ message, type }) {
    const slot = this.querySelector('[data-cart-footer-error]');
    if (!slot) return;

    slot.textContent = message;
    slot.hidden = false;

    if (type === 'cart_token_mismatch' || type === 'cart_lock') {
      setTimeout(() => window.location.reload(), 2000);
    }
  }

  applyActiveFooterError() {
    const state = theme.cart.errors.getFooter();
    if (!state) return;
    const slot = this.querySelector('[data-cart-footer-error]');
    if (!slot) return;
    slot.textContent = state.message;
    slot.hidden = false;
  }

  _clearFooterErrorSlot() {
    const slot = this.querySelector('[data-cart-footer-error]');
    if (!slot) return;
    slot.hidden = true;
    slot.textContent = '';
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', section: 'cart-drawer', selector: '[data-cart-drawer-content]' }];
  }

  setLoading(isLoading) {
    this.setAttribute('data-loading', isLoading ? 'true' : 'false');
  }

  renderContents(response) {
    this._renderSections(response.sections || response);
    this._updateHeaderBadge(response);
    this.drawer?.open();
  }

  _renderSections(sections) {
    this.getSectionsToRender().forEach(({ id, section, selector }) => {
      const html = sections[id] || sections[section];
      if (!html) return;

      const doc = theme.utils.domParser.parseFromString(html, 'text/html');

      this._updateElement(selector, doc.querySelector(selector));
      this._updateFooter(doc);
      this._updateTitle(doc);
      this._updateFreeShipping(doc);
    });
  }

  _updateTitle(doc) {
    const newTitle = doc.querySelector('.cart-drawer__title');
    const currentTitle = this.querySelector('.cart-drawer__title');
    if (newTitle && currentTitle) {
      currentTitle.innerHTML = newTitle.innerHTML;
    }
  }

  _updateFreeShipping(doc) {
    const newBar = doc.querySelector('.cart-free-shipping');
    const currentBar = this.querySelector('.cart-free-shipping');

    if (!newBar && currentBar) {
      currentBar.remove();
      return;
    }

    if (newBar && !currentBar) {
      this.querySelector('.cart-drawer__header')?.appendChild(newBar.cloneNode(true));
      return;
    }

    if (newBar && currentBar) {
      currentBar.innerHTML = newBar.innerHTML;
    }
  }

  _updateFooter(doc) {
    const newFooter = doc.querySelector('.cart-drawer__footer');
    const currentFooter = this.querySelector('.cart-drawer__footer');

    if (!newFooter && currentFooter) {
      currentFooter.remove();
      return;
    }

    if (newFooter && !currentFooter) {
      this.querySelector('[data-drawer-panel]')?.appendChild(newFooter.cloneNode(true));
      return;
    }

    if (newFooter && currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
    }
  }

  _updateElement(selector, newElement, mode = 'innerHTML') {
    const current = this.querySelector(selector);

    if (!newElement) {
      current?.remove();
      return;
    }

    if (!current) {
      this.querySelector('[data-drawer-panel]')?.appendChild(newElement.cloneNode(true));
      return;
    }

    if (mode === 'textContent') {
      current.textContent = newElement.textContent;
    } else {
      current.innerHTML = newElement.innerHTML;
    }
  }

  _resolveItemCount(response) {
    if (typeof response?.item_count === 'number') return response.item_count;

    const sections = response?.sections;
    if (!sections) return null;

    for (const { id, section } of this.getSectionsToRender()) {
      const html = sections[id] || sections[section];
      if (!html) continue;

      const doc = theme.utils.domParser.parseFromString(html, 'text/html');
      const countEl = doc.querySelector('[data-cart-item-count]');
      if (countEl) return parseInt(countEl.getAttribute('data-cart-item-count'), 10) || 0;
    }

    return null;
  }

  _updateHeaderBadge(response) {
    const headerBadge = document.getElementById('cart-count-badge');
    if (!headerBadge) return;

    const itemCount = this._resolveItemCount(response);

    if (itemCount === null) {
      theme.cart.fetch().then((cart) => this._updateHeaderBadge(cart)).catch((error) => console.error('[cart][silent]', error));
      return;
    }

    theme.utils.updateCartCount(itemCount);
  }

  async _handleCartUpdate(data) {
    if (data?.skipFetch) {
      if (data?.cartData) this._updateHeaderBadge(data.cartData);
      return;
    }

    if (data?.sections) {
      this._renderSections(data.sections);
      if (data?.cart) this._updateHeaderBadge(data.cart);
      this.applyActiveFooterError();
      this.querySelector('cart-items')?.applyActiveLineErrors?.();
      this.drawer?.refreshFocus();
      return;
    }

    try {
      const sectionIds = this.getSectionsToRender().map(({ section }) => section);
      const cart = await theme.cart.fetch({ sections: sectionIds });
      this._renderSections(cart.sections);

      if (data?.cart) this._updateHeaderBadge(data.cart);
      else this._updateHeaderBadge(cart);

      this.applyActiveFooterError();
      this.querySelector('cart-items')?.applyActiveLineErrors?.();
      this.drawer?.refreshFocus();
    } catch (error) {
      theme.pubsub.publish(theme.pubsub.events.CART_ERROR, { error });
    }
  }
}

customElements.define('cart-drawer', CartDrawer);
